import { supabase } from '@/integrations/supabase/client';

export interface RawProfileRow {
  id: string;
  full_name: string | null;
  profile_picture_url: string | null;
  username: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  main_academy_id: string | null;
  belt_rank: string | null;
  profession: string | null;
  is_premium: boolean | null;
  updated_at: string | null;
}

export interface RawConnectionRow {
  requester: string;
  recipient: string;
  status?: string;
}

export interface RawHobbyRow {
  profile_id: string;
  hobby_id: number;
}

export interface RawAcademyRow {
  name: string;
  city: string | null;
  state: string | null;
}

export interface LocationData {
  country: string;
  state: string;
  city: string;
}

export const connectionsRepository = {
  async getProfiles(excludeId: string, limit: number): Promise<RawProfileRow[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, profile_picture_url, username, country, state, city, main_academy_id, belt_rank, profession, is_premium, updated_at')
      .neq('id', excludeId)
      .order('updated_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as RawProfileRow[];
  },

  async getConnectionsByUser(userId: string): Promise<RawConnectionRow[]> {
    const { data, error } = await supabase
      .from('connections')
      .select('requester, recipient, status')
      .or(`requester.eq.${userId},recipient.eq.${userId}`);
    if (error) throw error;
    return (data ?? []) as RawConnectionRow[];
  },

  async getMyConnections(): Promise<{ user_id: string }[]> {
    const { data, error } = await supabase.rpc('get_my_connections');
    if (error) throw error;
    return (data ?? []) as { user_id: string }[];
  },

  async getAcceptedConnections(): Promise<RawConnectionRow[]> {
    const { data, error } = await supabase
      .from('connections')
      .select('requester, recipient')
      .eq('status', 'accepted');
    if (error) throw error;
    return (data ?? []) as RawConnectionRow[];
  },

  async getHobbiesByProfiles(profileIds: string[]): Promise<RawHobbyRow[]> {
    const { data, error } = await supabase
      .from('profile_hobbies')
      .select('profile_id, hobby_id')
      .in('profile_id', profileIds);
    if (error) throw error;
    return (data ?? []) as RawHobbyRow[];
  },

  async getAcademy(academyId: string): Promise<RawAcademyRow | null> {
    const { data, error } = await supabase
      .from('academies')
      .select('name, city, state')
      .eq('id', academyId)
      .maybeSingle();
    if (error) return null;
    return data as RawAcademyRow | null;
  },

  async sendConnectionRequest(requesterId: string, recipientId: string): Promise<void> {
    const { error } = await supabase
      .from('connections')
      .insert({ requester: requesterId, recipient: recipientId, status: 'pending' });
    if (error) throw error;
  },

  async updateLocation(userId: string, data: LocationData): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({ country: data.country, state: data.state, city: data.city })
      .eq('id', userId);
    if (error) throw error;
  },
};
